import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import { TableDetailsButton } from './table-actions';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getEntities as getRestaurants,
  setPageAction,
  setRowsPerPageAction,
} from '../../redux/actions/restaurantsActions';
import { useTranslation } from 'react-i18next';
import {
  actionListener,
  paginationCountLabel,
  propertyToggle,
} from '../../utils/helpers';

import RBISearchButton from '../RBISearchButton/RBISearchButton';
import RBISelectButton from '../RBISelectButton/RBISelectButton';
import {
  getGlobalCountries,
  getStates,
} from '../../redux/actions/regionsActions';
import store from '../../redux/store';
import { UI_SET_BRAND } from '../../redux/action-types/ui-types';
import { Button, Grid, Typography } from '@material-ui/core';
import AppConstants from '../../AppConstants';
import { textToSnakeCase } from '../../utils/utils';

const statusOptions = [
  { name: 'Open', value: 'Open' },
  { name: 'Closed', value: 'Closed' },
  { name: 'Projected', value: 'Projected' },
  { name: 'Temp Closed', value: 'Temp Closed' },
];
interface Column {
  id:
    | 'number'
    | 'address'
    | 'country'
    | 'city'
    | 'state'
    | 'status'
    | 'postal'
    | 'details';
  label: string | any;
  minWidth?: number;
  maxWidth?: number;
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  textAlign?: string | any;
  padding?: number;
  format?: (value: number) => string;
  actions?: [any];
}

interface Data {
  id: number;
  number: number;
  address: string;
  country: string;
  city: string;
  state: string;
  status: string;
  postal: string;
  details?: any;
  region?: string;
}

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  container: {
    maxHeight: '60vh',
    minHeight: '60vh',
  },
  dropDownSelector: {
    minWidth: 140,
  },
  divHorizontal: {
    display: 'flex',
  },
  buttonHeight: {
    height: '70%',
  },
}));

/**
 * Main table with restaurants info and view details button
 * @constructor
 * @returns Restaurants List Table Component
 */
export default function RestaurantList() {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const dispatch: any = useDispatch();

  const paginationConfig = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.paginationConfig,
  );

  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );

  const searchObject = {
    brand: selectedBrand,
    limit: paginationConfig.rowsPerPage,
  };
  const [searchRestaurant, setSearchRestaurant] = useState<any>({
    number: '',
    address: '',
    postal: '',
    city: '',
    state: '',
    stateName: '',
    country: '',
    status: '',
    region: '',
    countryName: '',
  });
  const searchClear = {
    number: '',
    address: '',
    postal: '',
    city: '',
    state: '',
    stateName: '',
    country: '',
    status: '',
    region: '',
    countryName: '',
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPageAction(newPage + 1));
    dispatch(
      getRestaurants({
        ...searchRestaurant,
        ...searchObject,
        page: newPage + 1,
      }),
    );
  };

  function createData({
    id,
    number,
    address,
    country,
    city,
    state,
    status,
    postal,
  }: Data): Data {
    return {
      id,
      number,
      address,
      country,
      city,
      state,
      status: t(`restaurant.status.${textToSnakeCase(status)}`, status),
      postal,
    };
  }
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setRowsPerPageAction(+event.target.value));
    dispatch(setPageAction(1));
    dispatch(
      getRestaurants({
        ...searchRestaurant,
        ...searchObject,
        limit: +event.target.value,
      }),
    );
  };

  /** Redirects to restaurant view based on restaurant index */
  const handleClick = (index: number) => {
    history.push(`/restaurants/${index}`);
  };
  const restaurantsresponse = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entities,
  );

  const { permissions, userInfo } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );

  useEffect(() => {
    if (permissions.length && selectedBrand && Object.keys(userInfo).length) {
      dispatch(setRowsPerPageAction(AppConstants.rowsPerPage));
      dispatch(
        getRestaurants({
          limit: AppConstants.rowsPerPage,
          brand: selectedBrand,
        }),
      );
      dispatch(setPageAction(1));
    }
  }, [dispatch, selectedBrand, permissions]);

  useEffect(() => {
    const unsubscribeSetBrand = actionListener(
      store,
      UI_SET_BRAND,
      function () {
        setSearchRestaurant({ ...searchClear });
      },
    );

    return () => {
      unsubscribeSetBrand();
    };
  });

  const rows = restaurantsresponse?.map((d: Data) => createData(d));
  const restaurants = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entities,
  );

  /** Handles click on filter apply button */
  const handleApplyButton = (name: string | undefined, value: any) => {
    setSearchRestaurant({
      ...searchRestaurant,
      [name as string]: propertyToggle(value, value, ''),
    });

    paginationConfig.page = 1;

    dispatch(
      getRestaurants({
        ...searchRestaurant,
        page: 1,
        limit: paginationConfig.rowsPerPage,
        brand: selectedBrand,
      }),
    );
  };
  const searchRestaurantClear = () => {
    setSearchRestaurant({
      ...searchClear,
    });
    dispatch(setRowsPerPageAction(AppConstants.rowsPerPage));
    dispatch(setPageAction(1));
    dispatch(
      getRestaurants({
        page: 1,
        limit: paginationConfig.rowsPerPage,
        brand: selectedBrand,
      }),
    );
  };

  const handleChange = (
    event: React.ChangeEvent<{ value: unknown; name?: any }>,
  ) => {
    if (event.target.name === 'country') {
      setSearchRestaurant({ ...searchRestaurant, country: '' });
    }
    setSearchRestaurant({
      ...searchRestaurant,
      [event.target.name]: event.target.value,
    });
  };

  const { globalCountries, states } = useSelector(
    (store: RootStateOrAny) => store.RegionsReducer,
  );
  useEffect(() => {
    dispatch(getGlobalCountries());
  }, []);

  useEffect(() => {
    const region = globalCountries.find(
      c => c.id === searchRestaurant.country,
    )?.region;
    const countryName = globalCountries.find(
      c => c.id === searchRestaurant.country,
    )?.name;

    const stateName = states?.find(c => c.id === searchRestaurant.state)?.name;
    setSearchRestaurant({ ...searchRestaurant, countryName, stateName });
    if (searchRestaurant.country !== '') {
      dispatch(getStates(region, searchRestaurant.country));
    }
  }, [searchRestaurant.country, searchRestaurant.state]);

  /** Each column has its own style values, maxwidth can be adjusted but being cautious cause more than 159 can make horizontal scroll on table */
  const columns: Column[] = [
    {
      id: 'number',
      label: (
        <RBISearchButton
          id={'number'}
          freeSolo={true}
          valueOption={searchRestaurant['number']}
          labelText={t('restaurant.list.gridLabels.restaurantNumber')}
          noOptions={true}
          onChange={handleChange}
          handleApplyButton={handleApplyButton}
          options={restaurants}
          width={140}
        />
      ),
      minWidth: 140,
      maxWidth: 159,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 1,
      paddingRight: 1,
    },
    {
      id: 'address',
      label: (
        <RBISearchButton
          id={'address'}
          valueOption={searchRestaurant['address']}
          labelText={t('restaurant.list.gridLabels.address')}
          noOptions={true}
          onChange={handleChange}
          handleApplyButton={handleApplyButton}
          options={restaurants}
          textButton={t('app.common.filter')}
          width={150}
        />
      ),
      minWidth: 140,
      maxWidth: 159,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      textAlign: 'center',
    },
    {
      id: 'country',
      label: (
        <div className={classes.dropDownSelector}>
          <RBISelectButton
            id={'country'}
            valueOption={searchRestaurant?.country}
            labelText={
              searchRestaurant['countryName']
                ? searchRestaurant['countryName']
                : t('restaurant.list.gridLabels.country')
            }
            onChangeInput={handleChange}
            width={140}
            options={[
              { value: '', name: 'Country' },
              ...globalCountries.map(country => ({
                value: country.id,
                name: t(`app.common.countries.${country.iso3}`, country.name),
              })),
            ]}
            handleApplyButton={handleApplyButton}
          />
        </div>
      ),
      minWidth: 140,
      maxWidth: 159,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 1,
    },
    {
      id: 'city',
      label: (
        <RBISearchButton
          id={'city'}
          valueOption={searchRestaurant['city']}
          labelText={t('restaurant.list.gridLabels.city')}
          // onChangeInput={() => {}}
          noOptions={true}
          onChange={handleChange}
          handleApplyButton={handleApplyButton}
          options={restaurants}
          textButton={t('app.common.filter')}
          width={140}
        />
      ),
      minWidth: 140,
      maxWidth: 159,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 1,
    },
    {
      id: 'state',
      label: (
        <div className={classes.dropDownSelector}>
          <RBISelectButton
            id={'state'}
            disabled={!searchRestaurant['country']}
            valueOption={
              states?.find(c => c.id === searchRestaurant['states'])?.name
            }
            labelText={
              searchRestaurant['stateName']
                ? searchRestaurant['stateName']
                : t('restaurant.list.gridLabels.state')
            }
            onChangeInput={handleChange}
            width={140}
            options={states.map(state => ({
              value: state.id,
              name: state.name,
            }))}
            handleApplyButton={handleApplyButton}
          />
        </div>
      ),
      minWidth: 140,
      maxWidth: 159,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 1,
    },
    {
      id: 'postal',
      label: (
        <RBISearchButton
          id={'postal'}
          valueOption={searchRestaurant['postal']}
          labelText={t('restaurant.info.edit.postalCode')}
          // onChangeInput={() => {}}
          noOptions={true}
          onChange={handleChange}
          handleApplyButton={handleApplyButton}
          options={restaurants}
          textButton={t('app.common.filter')}
          width={140}
        />
      ),
      minWidth: 140,
      maxWidth: 159,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 1,
    },

    {
      id: 'status',
      label: (
        <div className={classes.dropDownSelector}>
          <RBISelectButton
            id={'status'}
            valueOption={searchRestaurant['status']}
            labelText={
              searchRestaurant['status']
                ? t(
                    `restaurant.status.${textToSnakeCase(
                      searchRestaurant['status'],
                    )}`,
                    searchRestaurant['status'],
                  )
                : t('restaurant.list.gridLabels.status')
            }
            width={140}
            onChangeInput={handleChange}
            options={statusOptions.map(status => ({
              ...status,
              name: t(
                `restaurant.status.${textToSnakeCase(status.name)}`,
                status.name,
              ),
            }))}
            handleApplyButton={handleApplyButton}
          />
        </div>
      ),
      minWidth: 140,
      maxWidth: 159,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 1,
    },

    {
      id: 'details',
      label: t('restaurant.list.gridLabels.details'),
      minWidth: 30,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 1,
      paddingRight: 0,
      actions: [TableDetailsButton],
    },
  ];

  return (
    <Paper className={classes.root}>
      <Grid
        container
        spacing={1}
        direction="row"
        justify="flex-end"
        alignItems="flex-start"
        alignContent="stretch"
        wrap="nowrap"
      >
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            onClick={searchRestaurantClear}
          >
            {t('restaurant.list.gridLabels.clearBtn')}
          </Button>
        </Grid>
        <Grid item />
      </Grid>

      <TableContainer className={classes.container}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead style={{ backgroundColor: 'red !important' }}>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  style={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    paddingTop: column.paddingTop,
                    paddingBottom: column.paddingBottom,
                    paddingLeft: column.paddingLeft,
                    paddingRight: column.paddingRight,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length ? (
              rows.map(row => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                    {columns.map((column: Column) => {
                      if (column.actions) {
                        return (
                          <TableCell key={column.id}>
                            <div className={classes.divHorizontal}>
                              <TableDetailsButton
                                id={row['id']}
                                brand={selectedBrand}
                                clickIndex={handleClick}
                              />
                            </div>
                          </TableCell>
                        );
                      }
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id}>
                          {column.format && typeof value === 'number'
                            ? column.format(value)
                            : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={42}>
                  <div style={{ textAlign: 'center' }}>
                    <Typography>
                      {' '}
                      {t('restaurant.list.gridLabels.noMatchCriteria')}
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={AppConstants.rowsPerPageOptions}
        count={paginationConfig.total}
        rowsPerPage={paginationConfig.rowsPerPage}
        page={paginationConfig.page - 1}
        component={'div'}
        onPageChange={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        labelRowsPerPage={t('app.common.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          paginationCountLabel(
            from,
            to,
            count,
            t('app.common.paginationCountLabel'),
          )
        }
      />
    </Paper>
  );
}
